import { useState } from 'react';
import { Plus, Tag } from 'lucide-react';
import { useMapLandUnits, useCreateLandUnit, useUpdateLandUnit } from '../api';
import { landUnitLabel, useMapperPluginConfig } from '../mapperConfig';

export default function LandUnitsPanel() {
  const { data: configData } = useMapperPluginConfig();
  const label = landUnitLabel(configData?.config);
  const plural = landUnitLabel(configData?.config, true);
  const { data, isLoading } = useMapLandUnits();
  const createUnit = useCreateLandUnit();
  const updateUnit = useUpdateLandUnit();
  const [newName, setNewName] = useState('');
  const [groupTag, setGroupTag] = useState('');

  const units = data?.land_units ?? [];

  const handleCreateDrawn = async () => {
    const name = newName.trim();
    if (!name) return;
    await createUnit.mutateAsync({
      name,
      source: 'draw',
      parcel_refs: [],
      tags: groupTag ? [groupTag] : [],
      geometry: null,
    });
    setNewName('');
  };

  const handleTagGroup = async (unitId: string) => {
    const tag = groupTag.trim();
    if (!tag) return;
    const unit = units.find((u) => u.id === unitId);
    if (!unit) return;
    const tags = Array.from(new Set([...(unit.tags || []), tag]));
    await updateUnit.mutateAsync({ id: unitId, tags });
  };

  if (!configData?.config?.layers?.land_units?.enabled) return null;

  return (
    <div className="border-t border-gray-100 px-4 py-3 space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{plural}</h3>
        <span className="text-[10px] text-gray-400">{units.length}</span>
      </div>
      {isLoading ? (
        <p className="text-xs text-gray-400">Loading…</p>
      ) : units.length === 0 ? (
        <p className="text-xs text-gray-500">
          No {plural.toLowerCase()} yet. Draw or derive from cadastral parcels. This 0 is mapper inventory — header service chrome is a different store.
        </p>
      ) : (
        <ul className="space-y-1 max-h-40 overflow-y-auto">
          {units.map((u) => (
            <li key={u.id} className="flex items-center justify-between gap-2 text-xs">
              <span className="truncate font-medium text-gray-800">{u.name}</span>
              <button
                type="button"
                className="text-teal-600 hover:underline shrink-0"
                onClick={() => handleTagGroup(u.id)}
                title="Add group tag"
              >
                <Tag className="w-3 h-3 inline" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-1">
        <input
          className="flex-1 rounded border border-gray-200 px-2 py-1 text-xs"
          placeholder={`New ${label.toLowerCase()} name`}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />
        <button
          type="button"
          className="rounded bg-teal-600 px-2 py-1 text-xs text-white hover:bg-teal-700"
          onClick={handleCreateDrawn}
          disabled={createUnit.isPending}
        >
          <Plus className="w-3 h-3" />
        </button>
      </div>
      <input
        className="w-full rounded border border-gray-200 px-2 py-1 text-xs"
        placeholder="Group tag (optional)"
        value={groupTag}
        onChange={(e) => setGroupTag(e.target.value)}
      />
    </div>
  );
}
